"use client";

import { classNames } from "@/lib/util";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useResizeObserver } from "@wojtekmaj/react-hooks";
import type { PDFDocumentProxy } from "pdfjs-dist";
import React, { ErrorInfo, useCallback, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, State> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  async componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);

    // // You can also log the error to an error reporting service
    // // logErrorToMyService(error, errorInfo);
    // Sentry.captureException(error);
    // await Sentry.flush();
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div>
          <p>There was an error displaying the PDF.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const options = {
  cMapUrl: "/cmaps/",
  standardFontDataUrl: "/standard_fonts/",
};

const resizeObserverOptions = {};

const maxWidth = 800;

interface Props {
  file: File;
  // numPages: number;
  // containerRef: HTMLElement | null;
  // containerWidth: number;
}

const DisplayPDFFile: React.FC<Props> = ({ file }) => {
  return (
    <ErrorBoundary>
      <DisplayPDFFileInner file={file} />
    </ErrorBoundary>
  );
};

const DisplayPDFFileInner: React.FC<Props> = ({ file }) => {
  // const [file, setFile] = useState<PDFFile>(null);
  const [numPages, setNumPages] = useState<number>();
  const [page, setPage] = useState<number>(1);
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>();

  const onResize = useCallback<ResizeObserverCallback>((entries) => {
    const [entry] = entries;

    if (entry) {
      setContainerWidth(entry.contentRect.width);
    }
  }, []);

  useResizeObserver(containerRef, resizeObserverOptions, onResize);

  // function onFileChange(event: React.ChangeEvent<HTMLInputElement>): void {
  //   const { files } = event.target;

  //   if (files && files[0]) {
  //     setFile(files[0] || null);
  //   }
  // }

  function onDocumentLoadSuccess({
    numPages: nextNumPages,
  }: PDFDocumentProxy): void {
    setNumPages(nextNumPages);
  }

  console.log(
    "width",
    containerWidth ? Math.min(containerWidth, maxWidth) : maxWidth
  );

  return (
    <div className="Example sticky top-20 h-max w-full max-w-lg grow-0 min-xl:max-w-3xl">
      <header>
        <h4>{file.name}</h4>
      </header>
      <div className="group relative overflow-hidden rounded shadow-2xl">
        {/* <div>
          <label htmlFor="file">Load from file:</label>{" "}
          <input onChange={onFileChange} type="file" />
        </div> */}
        <div ref={setContainerRef}>
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            options={options}
          >
            <Page
              pageNumber={page > numPages! ? numPages : page}
              width={
                containerWidth ? Math.min(containerWidth, maxWidth) : maxWidth
              }
            />
          </Document>
        </div>

        {/* page controls */}
        <div className="absolute bottom-[2%] left-1/2 z-10 flex -translate-x-1/2 transform items-center justify-center gap-2 rounded-md border border-gray-100 bg-white p-1 text-black opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
          <button
            className={classNames(
              "group/button rounded p-3 hover:bg-gray-100",
              page <= 1 ? "opacity-50" : ""
            )}
            disabled={page <= 1}
            onClick={() => setPage((prev) => prev - 1)}
          >
            <ChevronLeftIcon className="w-5 transition group-hover/button:scale-125" />
          </button>
          <p className="shrink-0 text-xs">
            {page || (numPages ? 1 : "--")} of {numPages || "--"}
          </p>
          <button
            className={classNames(
              "group/button rounded p-3 hover:bg-gray-100",
              page >= numPages! ? "opacity-50" : ""
            )}
            disabled={page >= numPages!}
            onClick={() => setPage((prev) => prev + 1)}
          >
            <ChevronRightIcon className="w-5 transition group-hover/button:scale-125" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisplayPDFFile;
